import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { FaUserAlt, FaKey, FaDownload } from 'react-icons/fa';
import '../styles/Login.css';
import '../styles/ProfileCreation.css';
import Logo from "../components/Logo";
import backgroundVideo from "../assets/hero-background.mp4";

function ProfileCreation() {
  const [profileName, setProfileName] = useState('');
  const [profileUuid, setProfileUuid] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};
    if (!profileName.trim()) {
      newErrors.name = 'Profile name is required';
    }
    if (!profileUuid.trim()) {
      newErrors.uuid = 'Agent key is required';
    } else if (!/^[0-9a-f]{128}$/i.test(profileUuid.trim())) {
      newErrors.uuid = 'Invalid agent key format - must be 128 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateProfile = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const response = await api.post('/api/profiles/', {
        name: profileName,
        uuid: profileUuid.trim()
      });
      
      // Redirect to the chat page after successful profile creation
      navigate('/chat');
    } catch (error) {
      console.error('Error creating profile:', error);
      if (error.response?.data?.error) {
        // Handle specific error types returned from the API
        const errorMessage = error.response.data.error;
        if (errorMessage.includes("Profile with this name already exists")) {
          setErrors({ name: errorMessage });
        } else if (errorMessage.includes("agent key is already in use")) {
          setErrors({ uuid: errorMessage });
        } else {
          setErrors({ uuid: errorMessage });
        }
      } else {
        setErrors({ general: 'Failed to create profile. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container gradient-background">
      <video autoPlay loop muted playsInline className="login-background-video">
        <source src={backgroundVideo} type="video/mp4" />
      </video>
      <div className="login-overlay"></div>
      
      <header className="login-header-nav">
        <Logo size="small" showText={false} linkTo="/" />
      </header>

      <div className="login-content">
        <div className="login-card">
          <div className="login-header">
            <h2>Create Your Profile</h2>
            <p>You need to create a profile to use the application</p>
          </div>

          <div className="download-section">
            <div className="download-info">
              <FaDownload className="download-icon" />
              <div className="download-text">
                <p>Download triagz agent to get your key</p>
              </div>
            </div>
            <a 
              href="/downloads" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="download-link"
            >
              Download Agent
            </a>
          </div>
          
          <form onSubmit={handleCreateProfile} className="login-form">
            {errors.general && <div className="error-message">{errors.general}</div>}
            
            <div className="form-group">
              <label htmlFor="profile-name">Profile Name</label>
              <div className="input-group">
                <FaUserAlt className="input-icon" />
                <input
                  id="profile-name"
                  type="text"
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  placeholder="Enter profile name"
                  className={errors.name ? 'error' : ''}
                  autoFocus
                />
              </div>
              {errors.name && <div className="validation-errors"><span className="validation-error">{errors.name}</span></div>}
            </div>
            
            <div className="form-group">
              <label htmlFor="profile-uuid">Agent Key</label>
              <div className="input-group">
                <FaKey className="input-icon" />
                <input
                  id="profile-uuid"
                  type="text"
                  value={profileUuid}
                  onChange={(e) => setProfileUuid(e.target.value)}
                  placeholder="Enter key received from the agent"
                  className={errors.uuid ? 'error' : ''}
                />
              </div>
              {errors.uuid && <div className="validation-errors"><span className="validation-error">{errors.uuid}</span></div>}
            </div>
            
            <button 
              className="login-button" 
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? 'Creating...' : 'Create Profile'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ProfileCreation;